import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Circle, HStack, Spacer, TabPanel, TabPanels, Tabs, Text, VStack } from '@chakra-ui/react'
import { MdAddComment } from 'react-icons/md'
import { observer } from 'mobx-react'
import rootStore from '../../../../store/RootStore'
import NoComments from '../NoComments'
import { CommentCardGroup } from '../CommentCardGroup'
import { CustomTabList } from '../../../ui/CustomTabList'
import CommentForm from '../CommentForm'
import { LayoutContext } from '../../../ui/Layout/Layout'

const TAB_ITEMS = [
  {
    id: '1',
    label: 'Current',
    disabled: true,
  },
  {
    id: '2',
    label: 'All Charts',
  },
  {
    id: '3',
    label: '@Me',
  },
]

const CommentsPanel = observer(() => {
  const {
    commentStore: { turnOffCommentsEnabled, setIsFormVisible, chartId },
  } = rootStore

  const [, setTabItems] = useState(TAB_ITEMS)
  const { isRightPanelOpen } = useContext(LayoutContext)

  const handleCloseComments = useCallback(() => {
    setIsFormVisible(false)
    turnOffCommentsEnabled()
  }, [setIsFormVisible, turnOffCommentsEnabled])

  useEffect(() => {
    if (!isRightPanelOpen) {
      handleCloseComments()
    }
  }, [isRightPanelOpen, handleCloseComments])

  useEffect(() => {
    if (chartId) {
      const tabItem = { ...TAB_ITEMS?.find(tab => tab.id === '1') }
      tabItem.disabled = false
      setTabItems({
        ...TAB_ITEMS.filter(tab => tabItem.id !== tab.id),
        tabItem,
      })
    }
  }, [chartId])

  return (
    <Tabs w="100%">
      <VStack
        w="100%"
        top={0}
        alignItems="flex-start"
        p="30px 30px 0 30px"
        background="brand.white"
        zIndex={2}
      >
        <Header />
        <CustomTabList
          w="100%"
          items={TAB_ITEMS}
        />
      </VStack>
      <CommentForm
        m="0 0 20px 0"
        w="100%"
        p="12px 30px"
      />
      <TabPanels
        position="relative"
        p="8px 20px 20px"
        h={{ base: 'calc(100vh - 200px)', xl: 'calc(100vh - 106px)' }}
        overflowY="auto"
        overflowX="hidden"
      >
        <TabPanel p={0}>
          <TabCurrentContent />
        </TabPanel>
        <TabPanel p={0}>
          <TabAllComments />
        </TabPanel>
        <TabPanel p={0}>
          <TabAllComments onweredByAuth />
        </TabPanel>
      </TabPanels>
    </Tabs>
  )
})

CommentsPanel.propTypes = {}
const Header = observer(() => {
  const {
    commentStore: { isFormVisible, setIsFormVisible, chartId },
    authStore: { user },
  } = rootStore
  const handleToggleFormVisible = useCallback(() => {
    setIsFormVisible(!isFormVisible, false, undefined, user)
  }, [setIsFormVisible, isFormVisible, user])
  return (
    <HStack
      alignItems="center"
      w="100%"
    >
      <Text textStyle="h2">Comments</Text>
      <Spacer />
      <Circle
        background={!isFormVisible ? '#EEA254' : '#b95c2c'}
        onClick={handleToggleFormVisible}
        aria-label="Add Comment"
        cursor={chartId ? 'pointer' : 'not-allowed'}
        w="32px"
        h="32px"
        alignItems="center"
        justifyContent="center"
        _hover={{
          backgroundColor: 'brand.orange',
        }}
        pointerEvents={chartId ? 'all' : 'none'}
        opacity={chartId ? 1 : 0.3}
      >
        <MdAddComment
          color="white"
          size="13px"
        />
      </Circle>
    </HStack>
  )
})

const TabCurrentContent = observer(() => {
  const {
    commentStore: {
      loadingChartComments,
      chartComments,
      loadChartComments,
      chartId,
    },
  } = rootStore

  useEffect(() => {
    if (chartId) {
      loadChartComments()
    }
  }, [chartId, loadChartComments])

  if (loadingChartComments) {
    return <span>Loading comments...</span>
  }

  if (!loadingChartComments && !chartComments.comments) {
    return <NoComments />
  }

  return (
    <CommentCardGroup
      key={chartComments.chartId}
      chartId={chartComments.chartId}
      chartName={chartComments.chartName}
      chartFilter={chartComments.chartFilter}
      comments={chartComments.comments}
      mb="24px"
    />
  )
})

const TabAllComments = observer(({ onweredByAuth = false }) => {
  const {
    commentStore: { loadReportComments, loadingReportComments, reportComments },
    authStore,
  } = rootStore

  useEffect(() => {
    loadReportComments()
  }, [loadReportComments])

  const getComments = useMemo(
    () => comments =>
      onweredByAuth
        ? comments.filter(comment => comment.user.id === authStore.user?.id)
        : comments,
    [authStore.user?.id, onweredByAuth],
  )

  if (loadingReportComments) {
    return 'Loading'
  }

  if (reportComments?.length === 0) return <NoComments />

  return reportComments?.map((chartComments, index) => (
    <CommentCardGroup
      key={index}
      chartId={chartComments.chartId}
      chartName={chartComments.chartName}
      chartFilter={chartComments.chartFilter}
      comments={getComments(chartComments.comments)}
      mb="24px"
    />
  ))
})

export default CommentsPanel
